import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {
  EvaluationBandAdmin,
  EvaluationBandService,
} from '../../../core/services/evaluation-band.service';
import { SwalService } from '../../../core/services/utils/swal.service';
import { ErrorStateComponent } from '../../../components/ui/error-state/error-state.component';
import { ReadOnlyNoticeComponent } from '../../../components/ui/read-only-notice/read-only-notice.component';
import { AuthService } from '../../../core/services/utils/auth.service';

@Component({
  selector: 'app-admin-evaluation-bands',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ErrorStateComponent, ReadOnlyNoticeComponent],
  templateUrl: './evaluation-bands.component.html',
  styleUrl: './evaluation-bands.component.css',
})
export class EvaluationBandsComponent implements OnInit {
  private readonly service = inject(EvaluationBandService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);

  /**
   * Le barème se lit avec `referential.read` et se règle avec une autre permission. Sans `referential.write`, l'écran s'ouvre en consultation : champs
   * figés, gestes d'écriture retirés, et un bandeau qui le dit. Le back
   * revérifie : ce test n'est pas la protection, c'est l'honnêteté de l'écran.
   */
  readonly canWrite = inject(AuthService).hasPermission('referential.write');

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errored = signal(false);

  readonly bands = signal<EvaluationBandAdmin[]>([]);

  /**
   * Défauts de continuité, calculés en direct.
   *
   * Le serveur refuse déjà un paramétrage troué ; l'afficher pendant la saisie
   * évite d'aller au bout d'un formulaire pour se le voir rejeter. Le message
   * dit ce qui manque, pas seulement que c'est invalide.
   */
  readonly issues = computed<string[]>(() => {
    const ordered = [...this.bands()].sort((a, b) => a.minPercentage - b.minPercentage);
    if (ordered.length === 0) return [];

    const problems: string[] = [];

    for (const band of ordered) {
      if (band.minPercentage > band.maxPercentage) {
        problems.push(
          this.translate.instant('EVAL_BANDS.ERR_INVERTED', { code: band.code }),
        );
      }
    }

    if (ordered[0].minPercentage !== 0) {
      problems.push(this.translate.instant('EVAL_BANDS.ERR_START'));
    }

    if (ordered[ordered.length - 1].maxPercentage !== 100) {
      problems.push(this.translate.instant('EVAL_BANDS.ERR_END'));
    }

    for (let i = 1; i < ordered.length; i++) {
      const expected = ordered[i - 1].maxPercentage + 1;
      if (ordered[i].minPercentage !== expected) {
        problems.push(
          this.translate.instant('EVAL_BANDS.ERR_GAP', {
            previous: ordered[i - 1].maxPercentage,
            expected,
            actual: ordered[i].minPercentage,
          }),
        );
      }
    }

    return problems;
  });

  readonly canSave = computed(() => this.issues().length === 0 && !this.saving());

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.errored.set(false);

    this.service.list().subscribe({
      next: (res) => {
        this.bands.set(res?.data ?? []);
        this.loading.set(false);
      },
      error: () => {
        // Aucun palier de substitution : un paramétrage inventé se ferait
        // enregistrer par-dessus le vrai au premier clic.
        this.bands.set([]);
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }

  /** Met à jour un champ d'un palier sans perdre l'ordre d'affichage. */
  patch(id: number, changes: Partial<EvaluationBandAdmin>): void {
    this.bands.update((list) =>
      list.map((b) => (b.id === id ? { ...b, ...changes } : b)),
    );
  }

  onNumber(id: number, field: 'minPercentage' | 'maxPercentage', value: string): void {
    const parsed = Number(value);
    this.patch(id, { [field]: Number.isFinite(parsed) ? parsed : 0 } as Partial<EvaluationBandAdmin>);
  }

  save(): void {
    if (!this.canSave()) return;

    this.saving.set(true);
    this.service.save(this.bands()).subscribe({
      next: (res) => {
        this.bands.set(res?.data ?? this.bands());
        this.saving.set(false);
        this.swal.success(this.translate.instant('EVAL_BANDS.SAVED'));
      },
      error: (err) => {
        this.saving.set(false);
        this.swal.error(this.messageFrom(err));
      },
    });
  }

  /** Message serveur dans la langue de l'utilisateur, via son code d'erreur. */
  private messageFrom(err: unknown): string {
    const body = (err as { error?: { code?: string; message?: string } })?.error;

    if (body?.code) {
      const key = `EVAL_BANDS.ERRORS.${body.code}`;
      const translated = this.translate.instant(key);
      if (translated !== key) return translated;
    }

    return body?.message || this.translate.instant('EVAL_BANDS.SAVE_ERROR');
  }
}
